(() => {
  if (customElements.get('cart-drawer-upsell')) return;

  const sections = ['cart-drawer', 'cart-icon-bubble'];
  // Add-on opt-out is stored in localStorage (falls back to sessionStorage) so a
  // shopper who switched an add-on OFF keeps it off for the whole purchase, across
  // tabs and page loads. It is cleared only when the cart becomes empty
  // (resetDefaultAddonPreferences), so the next purchase starts with the add-ons ON.
  const addonStore = {
    get(key) {
      try { return window.localStorage.getItem(key); } catch (e) {}
      try { return window.sessionStorage.getItem(key); } catch (e) {}
      return null;
    },
    set(key, value) {
      try { window.localStorage.setItem(key, value); } catch (e) {}
      try { window.sessionStorage.setItem(key, value); } catch (e) {}
    },
    remove(key) {
      try { window.localStorage.removeItem(key); } catch (e) {}
      try { window.sessionStorage.removeItem(key); } catch (e) {}
    },
  };
  window.awAddonStore = addonStore;
  let cartUpsellQueue = Promise.resolve();
  let defaultAddonTimer;
  let defaultAddonSyncRunning = false;

  const resetDefaultAddonPreferences = () => {
    document.querySelectorAll('#AwDefaultCartAddons [data-storage-key]').forEach((item) => {
      addonStore.remove(item.dataset.storageKey);
    });
  };

  const scheduleDefaultAddonSync = () => {
    clearTimeout(defaultAddonTimer);
    defaultAddonTimer = setTimeout(() => {
      // Run on the same queue as the toggle switches so a default re-add can never
      // overlap (and undo) a removal the shopper just asked for.
      cartUpsellQueue = cartUpsellQueue.then(syncDefaultAddons).catch(() => {});
    }, 0);
  };

  const syncDefaultAddons = async () => {
    if (defaultAddonSyncRunning) return;
    const candidates = [...document.querySelectorAll('cart-drawer-upsell[data-toggle="true"]')]
      .filter((item) =>
        item.isConnected &&
        item.dataset.defaultSelected === 'true' &&
        item.dataset.selected !== 'true' &&
        item.dataset.loading !== 'true' &&
        addonStore.get(item.dataset.storageKey) !== 'off'
      );
    if (!candidates.length) return;

    defaultAddonSyncRunning = true;
    candidates.forEach((item) => {
      item.dataset.selected = 'true';
      item.dataset.loading = 'true';
      item.syncAccessibility?.();
      item.toggleButton?.setAttribute('aria-busy', 'true');
    });

    try {
      const items = candidates
        .map((candidate) => ({
          id: Number(candidate.variantInput?.value || candidate.dataset.id),
          quantity: 1,
          properties: { _aw_cart_addon: candidate.dataset.addonKey },
        }))
        .filter((item) => item.id);

      if (!items.length) return;
      const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items,
          sections,
          sections_url: window.location.pathname,
        }),
      });
      const state = await response.json();
      if (!response.ok) throw new Error(state.description || 'Could not add cart add-ons');
      const drawer = document.querySelector('cart-drawer');
      if (drawer?.renderContents && state.sections) {
        drawer.renderContents(state, { open: drawer.classList.contains('active') });
      }
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'cart-drawer-upsell',
        cartData: state,
        renderedSections: ['cart-drawer'],
      });
    } catch (error) {
      console.error('Sync default cart add-ons:', error);
      candidates.forEach((item) => {
        item.dataset.selected = 'false';
        item.syncAccessibility?.();
      });
    } finally {
      candidates.forEach((item) => {
        item.dataset.loading = 'false';
        item.toggleButton?.removeAttribute('aria-busy');
      });
      defaultAddonSyncRunning = false;
    }
  };

  class CartDrawerUpsell extends HTMLElement {
    connectedCallback() {
      this.toggleButton = this.querySelector('.upsell-toggle-btn');
      this.form = this.querySelector('form');
      this.variantInput = this.form?.querySelector('[name="id"]');
      this.variantSelects = [...this.querySelectorAll('.variant-dropdown')];
      this.variants = this.readVariants();

      this.toggleButton?.setAttribute('type', 'button');
      this.toggleButton?.setAttribute('role', 'switch');
      this.syncAccessibility();
      this.toggleButton?.addEventListener('click', this.onToggle);
      this.variantSelects.forEach((select) => select.addEventListener('change', this.onVariantChange));

      if (
        this.dataset.toggle === 'true' &&
        this.dataset.selected === 'true' &&
        Number(this.dataset.cartQuantity || 0) > 1
      ) {
        this.normalizeQuantity();
      }

      if (
        this.dataset.toggle === 'true' &&
        this.dataset.defaultSelected === 'true' &&
        this.dataset.selected !== 'true' &&
        addonStore.get(this.dataset.storageKey) !== 'off'
      ) {
        scheduleDefaultAddonSync();
      }
    }

    disconnectedCallback() {
      this.toggleButton?.removeEventListener('click', this.onToggle);
      this.variantSelects.forEach((select) => select.removeEventListener('change', this.onVariantChange));
    }

    readVariants() {
      const json = this.querySelector('.upsell__variant-picker script[type="application/json"]');
      if (!json) return [];
      try {
        return JSON.parse(json.textContent);
      } catch {
        return [];
      }
    }

    onVariantChange = () => {
      if (!this.variants.length) return;
      const options = this.variantSelects.map((select) => select.value);
      const variant = this.variants.find((item) =>
        options.every((value, index) => item.options[index] === value)
      );
      if (!variant?.available) return;
      if (this.variantInput) this.variantInput.value = variant.id;
      this.dataset.id = variant.id;
    };

    onToggle = (event) => {
      event.preventDefault();
      event.stopPropagation();
      const shouldAdd = this.dataset.selected !== 'true';
      addonStore.set(this.dataset.storageKey, shouldAdd ? 'on' : 'off');
      this.dataset.selected = String(shouldAdd);
      this.syncAccessibility();
      this.updateCart(shouldAdd, true);
    };

    updateCart(shouldAdd, announce) {
      if (this.dataset.loading === 'true') return;
      this.dataset.loading = 'true';
      this.toggleButton?.setAttribute('aria-busy', 'true');
      this.toggleButton?.setAttribute('disabled', '');

      cartUpsellQueue = cartUpsellQueue
        .then(() => shouldAdd ? this.addItem() : this.removeItem())
        .then((state) => {
          if (!state) {
            this.dataset.loading = 'false';
            this.toggleButton?.removeAttribute('aria-busy');
            this.toggleButton?.removeAttribute('disabled');
            return;
          }
          this.renderCart(state);
          if (announce) this.announce(shouldAdd ? 'Added to cart' : 'Removed from cart');
        })
        .catch((error) => {
          console.error('Cart drawer upsell:', error);
          this.dataset.selected = String(!shouldAdd);
          this.syncAccessibility();
          this.dataset.loading = 'false';
          this.toggleButton?.removeAttribute('aria-busy');
          this.toggleButton?.removeAttribute('disabled');
        });
    }

    async addItem() {
      const id = Number(this.variantInput?.value || this.dataset.id);
      if (!id) throw new Error('No available upsell variant');

      const existing = await this.findCartItem(id);
      if (existing) {
        if (existing.quantity === 1) return null;
        return this.changeItem(existing.key, 1);
      }

      const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{
            id,
            quantity: 1,
            properties: { _aw_cart_addon: this.dataset.addonKey },
          }],
          sections,
          sections_url: window.location.pathname,
        }),
      });
      const state = await response.json();
      if (!response.ok) throw new Error(state.description || 'Could not add upsell');
      return state;
    }

    async removeItem() {
      // Remove EVERY line of this add-on product (any variant, with or without the
      // _aw_cart_addon property) so a stray/duplicate line can never keep it "on".
      const lines = await this.findCartLines();
      if (!lines.length) return null;
      if (lines.length === 1) return this.changeItem(lines[0].key, 0);

      const updates = Object.fromEntries(lines.map((line) => [line.key, 0]));
      const response = await fetch(window.routes?.cart_update_url || '/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates, sections, sections_url: window.location.pathname }),
      });
      const state = await response.json();
      if (!response.ok) throw new Error(state.description || 'Could not remove upsell');
      return state;
    }

    async findCartLines() {
      const cartResponse = await fetch(window.routes?.cart_url ? `${window.routes.cart_url}.js` : '/cart.js', {
        headers: { Accept: 'application/json' },
      });
      if (!cartResponse.ok) throw new Error('Could not read cart');
      const cart = await cartResponse.json();
      const handle = this.dataset.handle;
      const variantIds = new Set(
        (this.variants.length ? this.variants.map((v) => Number(v.id)) : [])
          .concat([Number(this.variantInput?.value || this.dataset.id)])
          .filter(Boolean)
      );
      return cart.items.filter((line) =>
        line.properties?._aw_cart_addon === this.dataset.addonKey ||
        variantIds.has(Number(line.variant_id)) ||
        (handle && line.handle === handle)
      );
    }

    async findCartItem(id) {
      const cartResponse = await fetch(window.routes?.cart_url ? `${window.routes.cart_url}.js` : '/cart.js', {
        headers: { Accept: 'application/json' },
      });
      if (!cartResponse.ok) throw new Error('Could not read cart');
      const cart = await cartResponse.json();
      return (
        cart.items.find((line) =>
          Number(line.variant_id) === id &&
          (line.properties?._aw_cart_addon === this.dataset.addonKey || !line.properties?._aw_cart_addon)
        ) || null
      );
    }

    async changeItem(key, quantity) {
      const response = await fetch(window.routes?.cart_change_url || '/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: key,
          quantity,
          sections,
          sections_url: window.location.pathname,
        }),
      });
      const state = await response.json();
      if (!response.ok) throw new Error(state.description || 'Could not remove upsell');
      return state;
    }

    normalizeQuantity() {
      if (this.dataset.loading === 'true') return;
      this.dataset.loading = 'true';
      cartUpsellQueue = cartUpsellQueue
        .then(async () => {
          // Collapse every line of this add-on into a single line with quantity 1.
          const lines = await this.findCartLines();
          if (!lines.length) return null;
          if (lines.length === 1 && lines[0].quantity === 1) return null;
          const updates = {};
          lines.forEach((line, index) => { updates[line.key] = index === 0 ? 1 : 0; });
          const response = await fetch(window.routes?.cart_update_url || '/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ updates, sections, sections_url: window.location.pathname }),
          });
          const state = await response.json();
          if (!response.ok) throw new Error(state.description || 'Could not normalize upsell');
          return state;
        })
        .then((state) => {
          if (state) this.renderCart(state);
          else this.dataset.loading = 'false';
        })
        .catch((error) => {
          console.error('Normalize cart drawer upsell:', error);
          this.dataset.loading = 'false';
        });
    }

    renderCart(state) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer?.renderContents && state.sections) {
        drawer.renderContents(state, { open: drawer.classList.contains('active') });
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-drawer-upsell',
          cartData: state,
          renderedSections: ['cart-drawer'],
        });
        return;
      }
      window.location.reload();
    }

    syncAccessibility() {
      this.toggleButton?.setAttribute('aria-checked', String(this.dataset.selected === 'true'));
      this.toggleButton?.setAttribute(
        'aria-label',
        `${this.dataset.selected === 'true' ? 'Remove' : 'Add'} ${this.dataset.handle || 'product'}`
      );
    }

    announce(message) {
      const region = document.getElementById('CartDrawer-LiveRegionText');
      if (!region) return;
      region.textContent = message;
    }
  }

  customElements.define('cart-drawer-upsell', CartDrawerUpsell);

  // A default-selected add-on gets re-added automatically (see scheduleDefaultAddonSync)
  // unless the shopper explicitly opted out via the toggle switch, which is the only
  // place that used to record `sessionStorage[storageKey] = 'off'`. Removing the same
  // add-on line through the plain cart quantity/remove controls (cart page or drawer)
  // never touched that flag, so the item kept coming back on the next render. Listen
  // for any cart mutation and, if a selected add-on's line is gone, record the opt-out
  // the same way the toggle does.
  subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
    if (!event?.cartData?.items) return;
    // /cart/add.js responses only list the lines that were just added — not the
    // whole cart. Only a full cart payload (has item_count/token) can tell us that
    // an add-on line is really gone.
    if (typeof event.cartData.item_count !== 'number' && !event.cartData.token) return;
    const remainingAddonKeys = new Set(
      event.cartData.items
        .filter((item) => item.properties?._aw_cart_addon)
        .map((item) => item.properties._aw_cart_addon)
    );
    const remainingHandles = new Set(event.cartData.items.map((item) => item.handle));
    document.querySelectorAll('cart-drawer-upsell[data-toggle="true"][data-selected="true"]').forEach((el) => {
      const addonKey = el.dataset.addonKey;
      if (!addonKey || remainingAddonKeys.has(addonKey)) return;
      if (el.dataset.handle && remainingHandles.has(el.dataset.handle)) return;
      el.dataset.selected = 'false';
      addonStore.set(el.dataset.storageKey, 'off');
      el.syncAccessibility?.();
    });
  });

  let orphanCleanupRunning = false;
  const removeOrphanAddons = async () => {
    if (orphanCleanupRunning) return;
    // Read the state element that is re-rendered with the drawer contents. The
    // attributes on the outer #CartDrawer div are only set at page load (Dawn's
    // renderContents swaps innerHTML), so they must never be trusted here.
    const drawerState = document.querySelector('#AwCartState');
    if (!drawerState || drawerState.dataset.awEffectiveEmpty !== 'true') return;
    if (Number(drawerState.dataset.awActualCount || 0) === 0) {
      resetDefaultAddonPreferences();
      return;
    }
    orphanCleanupRunning = true;
    try {
      const cartResponse = await fetch(window.routes?.cart_url ? `${window.routes.cart_url}.js` : '/cart.js', {
        headers: { Accept: 'application/json' },
      });
      if (!cartResponse.ok) return;
      const cart = await cartResponse.json();
      if (cart.item_count === 0) {
        resetDefaultAddonPreferences();
        return;
      }
      const addonItems = cart.items.filter((item) => item.properties?._aw_cart_addon);
      const mainItems = cart.items.filter((item) => !item.properties?._aw_cart_addon);
      if (!addonItems.length || mainItems.length) return;

      const updates = Object.fromEntries(addonItems.map((item) => [item.key, 0]));
      const response = await fetch(window.routes?.cart_update_url || '/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          updates,
          sections,
          sections_url: window.location.pathname,
        }),
      });
      const state = await response.json();
      if (!response.ok) return;
      if (state.item_count === 0) resetDefaultAddonPreferences();
      const drawer = document.querySelector('cart-drawer');
      if (drawer?.renderContents && state.sections) {
        drawer.renderContents(state, { open: drawer.classList.contains('active') });
      }
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'cart-drawer-upsell',
        cartData: state,
        renderedSections: ['cart-drawer'],
      });
    } catch (error) {
      console.error('Remove orphan cart add-ons:', error);
    } finally {
      orphanCleanupRunning = false;
    }
  };

  removeOrphanAddons();

  const cartDrawerRoot = document.querySelector('#CartDrawer');
  if (cartDrawerRoot) new MutationObserver((mutations) => {
    if (mutations.some((mutation) => mutation.addedNodes.length || mutation.removedNodes.length)) {
      removeOrphanAddons();
    }
  }).observe(cartDrawerRoot, { childList: true, subtree: true });

  window.handleDiscountFormChange = (event) => {
    event.currentTarget.closest('.cart-discount-form')?.classList.remove('has-error');
  };

  window.handleDiscountForm = (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    const input = form.querySelector('[name="cart-discount-field"]');
    const code = input?.value.trim();
    if (!code) {
      form.classList.add('has-error');
      input?.focus();
      return;
    }
    window.location.assign(`/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(window.location.pathname)}`);
  };
})();
